// TelcoTrack — Křížení sítí: katalog oficiálních portálů provozovatelů.
// Podání má jít přes portál (ne jen datovka/e-mail). Odpovědi typicky e-mailem.

import { normalizeIco } from './authority-overlay-identity.constants';

/** Jak adapter odesílá žádost. */
export const NETWORK_CROSSING_SUBMIT_METHODS = [
  'web_form',
  'geoportal',
  'e_portal_login',
  'email_fallback',
  'isds_fallback',
  'unknown',
] as const;
export type NetworkCrossingSubmitMethod = (typeof NETWORK_CROSSING_SUBMIT_METHODS)[number];

/** Kanál, kterým typicky dorazí odpověď / potvrzení. */
export const NETWORK_CROSSING_REPLY_CHANNELS = [
  'email',
  'portal_download',
  'sms_password_email',
  'isds',
  'unknown',
] as const;
export type NetworkCrossingReplyChannel = (typeof NETWORK_CROSSING_REPLY_CHANNELS)[number];

export const NETWORK_CROSSING_AUTOMATION_STATUSES = [
  /** Adapter připraví balíček + deep-link; odeslání na portálu zatím asistované / RPA. */
  'assisted',
  /** HTTP integrace existuje (nebo je připravená za credentials). */
  'http_ready',
  /** Jen katalog + TODO — žádná implementace submit. */
  'stub',
  /** Oficiální API není; automatizace by porušila ToS / captcha. */
  'manual_only',
] as const;
export type NetworkCrossingAutomationStatus = (typeof NETWORK_CROSSING_AUTOMATION_STATUSES)[number];

export type NetworkCrossingSubjectHint =
  | 'elektrina'
  | 'plyn'
  | 'vodovod'
  | 'kanalizace'
  | 'elektronicke_komunikace'
  | 'teplo'
  | 'produktovody'
  | 'custom_sit';

export interface NetworkCrossingOperatorPortal {
  id: string;
  labelCs: string;
  ico: string | null;
  /** Subjektové klíče katalogu křížení, ke kterým se typicky váže. */
  subjectHints: readonly NetworkCrossingSubjectHint[];
  /** Needle v názvu (fold) pro mapování z DTM / ARES. */
  nameNeedles: readonly string[];
  portalUrl: string;
  /**
   * Portál žádosti o vytyčení, pokud je veřejně jiný než `portalUrl`.
   * Když chybí, vytyčení použije `portalUrl` a do evidence se zapíše účel `vytyceni`.
   */
  vytyceniPortalUrl?: string | null;
  statusUrl: string | null;
  docsUrl: string | null;
  submitMethod: NetworkCrossingSubmitMethod;
  replyChannel: NetworkCrossingReplyChannel;
  automationStatus: NetworkCrossingAutomationStatus;
  /** Regex (string) pro extrakci čísla žádosti z e-mailu / portálu. */
  requestIdPatterns: readonly string[];
  notesCs: string;
  risksCs: string;
}

/**
 * Provozovatelé běžně řešené u FTTH / křížení sítí v CZ.
 * Ověřeno proti veřejným stránkám provozovatelů (2026-09).
 */
export const NETWORK_CROSSING_OPERATOR_PORTALS: readonly NetworkCrossingOperatorPortal[] = [
  {
    id: 'cez_distribuce',
    labelCs: 'ČEZ Distribuce',
    ico: '24729035',
    subjectHints: ['elektrina', 'elektronicke_komunikace'],
    nameNeedles: ['cez distribuce', 'cez distrib'],
    portalUrl: 'https://geoportal.cezdistribuce.cz/geoportal.ses/ves.aspx',
    vytyceniPortalUrl:
      'https://www.cezdistribuce.cz/cs/pro-zakazniky/potrebuji-vyresit/stavajici-pripojeni/vytyceni-zarizeni',
    statusUrl: 'https://geoportal.cezdistribuce.cz/geoportal.ses/ves.aspx',
    docsUrl:
      'https://www.cezdistribuce.cz/cs/pro-zakazniky/potrebuji-vyresit/stavajici-pripojeni/sdeleni-o-existenci-siti',
    submitMethod: 'geoportal',
    replyChannel: 'email',
    automationStatus: 'assisted',
    requestIdPatterns: [
      'cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)',
      'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)',
      'request\\s*(?:no|number|#)?[:\\s]*([A-Z0-9\\-/]+)',
    ],
    notesCs:
      'Geoportál SES — zákres polygonu (max ~700×700 m), typ žádosti, výstup na e-mail. Stav žádosti v portálu.',
    risksCs: 'ASP.NET session + mapa; captcha/ToS; žádné veřejné API. HTTP scrape křehké.',
  },
  {
    id: 'egd',
    labelCs: 'EG.D',
    ico: '28085400',
    subjectHints: ['elektrina', 'plyn'],
    nameNeedles: ['eg.d', 'egd', 'e.on distribuce', 'eon distribuce'],
    portalUrl: 'https://www.egd.cz/zadost/sdeleni-k-existenci-elektrickych-siti',
    statusUrl: null,
    docsUrl: 'https://www.egd.cz/zadost/sdeleni-k-existenci-elektrickych-siti',
    submitMethod: 'web_form',
    replyChannel: 'email',
    automationStatus: 'assisted',
    requestIdPatterns: ['cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)', 'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)'],
    notesCs: 'Online formulář + Geoportál; v části území i plyn+elektřina najednou. Lhůta 30/60 dnů.',
    risksCs: 'Registrace doporučena; mapa + interaktivní formulář bez veřejného API.',
  },
  {
    id: 'gasnet',
    labelCs: 'GasNet',
    ico: '27295567',
    subjectHints: ['plyn'],
    nameNeedles: ['gasnet'],
    portalUrl: 'https://www.gasnet.cz/cs/distribuce-plynu-online/',
    vytyceniPortalUrl: 'https://www.gasnet.cz/dalsi-sluzby/pro-stavare-a-projektanty/vytyceni-plynovodni-site',
    statusUrl: 'https://www.gasnet.cz/cs/distribuce-plynu-online/',
    docsUrl: 'https://www.gasnet.cz/dalsi-sluzby/pro-stavare-a-projektanty/stanovisko-k-existenci-site',
    submitMethod: 'web_form',
    replyChannel: 'sms_password_email',
    automationStatus: 'assisted',
    requestIdPatterns: [
      'cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)',
      'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)',
      'zadost\\s+c\\.?\\s*([A-Z0-9\\-/]+)',
    ],
    notesCs:
      'DPO (Distribuce plynu online). Potvrzení e-mailem; hotové vyjádření = e-mail + SMS jednorázové heslo.',
    risksCs: 'SMS heslo blokuje plně automatické stažení; login/session; ToS.',
  },
  {
    id: 'prazska_plynarenska',
    labelCs: 'Pražská plynárenská Distribuce',
    ico: '27464976',
    subjectHints: ['plyn'],
    nameNeedles: ['prazska plynarenska', 'ppdistribuce', 'ppd'],
    portalUrl: 'https://e-portal.ppdistribuce.cz/',
    statusUrl: 'https://e-portal.ppdistribuce.cz/',
    docsUrl: 'https://ppdistribuce.cz/sluzby/eportal',
    submitMethod: 'e_portal_login',
    replyChannel: 'portal_download',
    automationStatus: 'stub',
    requestIdPatterns: ['cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)', 'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)'],
    notesCs: 'ePortál — existence sítí + PD; registrace volitelná; notifikační e-mail + stažení z portálu.',
    risksCs: 'Účet / session; TODO adapter. Fallback datovka/e-mail.',
  },
  {
    id: 'cetin',
    labelCs: 'CETIN',
    ico: '04084063',
    subjectHints: ['elektronicke_komunikace'],
    nameNeedles: ['cetin', 'nordic telecom'],
    portalUrl: 'https://portal.cetin.cz/web/dokumentace-site/zadani-zadosti-o-vyjadreni',
    vytyceniPortalUrl: 'https://www.cetin.cz/o-nas/sit-cetin/vytyceni-infrastruktury',
    statusUrl: 'https://portal.cetin.cz/web/dokumentace-site/zadani-zadosti-o-vyjadreni',
    docsUrl: 'https://www.cetin.cz/o-nas/sit-cetin/vyjadreni-o-poloze-site',
    submitMethod: 'web_form',
    replyChannel: 'email',
    automationStatus: 'assisted',
    requestIdPatterns: [
      'cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)',
      'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)',
      'vyjadreni\\s+c\\.?\\s*([A-Z0-9\\-/]+)',
    ],
    notesCs:
      'Online žádost o vyjádření o poloze SEK (zdarma). Odpověď téměř obratem na e-mail; stav v portálu.',
    risksCs: 'Mapový zákres; žádné veřejné B2B API; ToS / bot protection.',
  },
  {
    id: 'tmobile',
    labelCs: 'T-Mobile',
    ico: '64949681',
    subjectHints: ['elektronicke_komunikace'],
    nameNeedles: ['t-mobile', 'tmobile', 't mobile'],
    portalUrl: 'https://ochranasiti.t-mobile.cz/vyjadreni/',
    statusUrl: 'https://ochranasiti.t-mobile.cz/vyjadreni/',
    docsUrl: 'https://ochranasiti.t-mobile.cz/',
    submitMethod: 'web_form',
    replyChannel: 'email',
    automationStatus: 'assisted',
    requestIdPatterns: ['cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)', 'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)'],
    notesCs: 'ochranasiti.t-mobile.cz — registrace uživatele; vyjádření do 30 dnů na e-mail.',
    risksCs: 'Registrace + starší UI; GISOFT provoz; captcha možné.',
  },
  {
    id: 'vodafone',
    labelCs: 'Vodafone',
    ico: '25788001',
    subjectHints: ['elektronicke_komunikace'],
    nameNeedles: ['vodafone'],
    portalUrl: 'https://zadostovyjadreni.vodafone.cz/',
    statusUrl: 'https://zadostovyjadreni.vodafone.cz/',
    docsUrl: 'https://zadostovyjadreni.vodafone.cz/',
    submitMethod: 'web_form',
    replyChannel: 'email',
    automationStatus: 'stub',
    requestIdPatterns: ['cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)', 'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)'],
    notesCs: 'Portál žádostí o vyjádření k SEK. Detailní API není veřejné.',
    risksCs: 'TODO adapter; ověřit přihlášení a mapu.',
  },
  {
    id: 'scvk',
    labelCs: 'SČVK',
    ico: '46349275',
    subjectHints: ['vodovod', 'kanalizace'],
    nameNeedles: ['scvk', 'severoceske vodovody', 'severoceske vodovody a kanalizace'],
    portalUrl: 'https://www.scvk.cz/vyjadrovaci-portal',
    statusUrl: 'https://www.scvk.cz/vyjadrovaci-portal',
    docsUrl: 'https://www.scvk.cz/vyjadrovaci-portal',
    submitMethod: 'web_form',
    replyChannel: 'email',
    automationStatus: 'stub',
    requestIdPatterns: ['cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)', 'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)'],
    notesCs: 'Vyjadřovací portál SČVK — existence sítí / PD / napojení.',
    risksCs: 'Lokální vodárny mají vlastní portály; SČVK je jeden z mnoha.',
  },
  {
    id: 'pre_distribuce',
    labelCs: 'PREdistribuce',
    ico: '27341224',
    subjectHints: ['elektrina'],
    nameNeedles: ['predistribuce', 'pre distribuce', 'prazska energetika'],
    portalUrl: 'https://www.predistribuce.cz/',
    statusUrl: null,
    docsUrl: 'https://www.predistribuce.cz/',
    submitMethod: 'unknown',
    replyChannel: 'email',
    automationStatus: 'stub',
    requestIdPatterns: ['cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)', 'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)'],
    notesCs: 'Praha — ověřit aktuální geoportál / e-podatelnu před implementací adapteru.',
    risksCs: 'TODO: potvrdit oficiální URL podání existence sítí.',
  },
] as const;

const BY_ID = new Map(NETWORK_CROSSING_OPERATOR_PORTALS.map((row) => [row.id, row]));
const BY_ICO = new Map(
  NETWORK_CROSSING_OPERATOR_PORTALS.filter((row) => row.ico).map((row) => [row.ico as string, row]),
);

export function networkCrossingOperatorById(id: string | null | undefined): NetworkCrossingOperatorPortal | null {
  const key = (id ?? '').trim();
  if (!key) return null;
  return BY_ID.get(key) ?? null;
}

export function networkCrossingOperatorByIco(ico: string | null | undefined): NetworkCrossingOperatorPortal | null {
  const key = normalizeIco(ico);
  if (!key) return null;
  return BY_ICO.get(key) ?? null;
}

export function foldNetworkCrossingName(value: string | null | undefined): string {
  return (value ?? '')
    .toLocaleLowerCase('cs')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Mapuje název / IČO z DTM nebo kontaktu na portálový řádek. */
export function resolveNetworkCrossingOperator(input: {
  ico?: string | null;
  name?: string | null;
  operatorId?: string | null;
}): NetworkCrossingOperatorPortal | null {
  const byId = networkCrossingOperatorById(input.operatorId);
  if (byId) return byId;
  const byIco = networkCrossingOperatorByIco(input.ico);
  if (byIco) return byIco;
  const folded = foldNetworkCrossingName(input.name);
  if (!folded) return null;
  const hits = NETWORK_CROSSING_OPERATOR_PORTALS.filter((row) =>
    row.nameNeedles.some((needle) => folded.includes(needle)),
  );
  return hits.length === 1 ? hits[0] : null;
}

/**
 * Known catalog operators (CETIN, ČEZ, …) must use the operator portal.
 * Non-catalog subjects (obec, unknown DTM owner, …) keep datovka / e-mail.
 */
export function networkCrossingRequiresPortalChannel(input: {
  ico?: string | null;
  name?: string | null;
  operatorId?: string | null;
}): boolean {
  return !!resolveNetworkCrossingOperator(input);
}

export const NETWORK_CROSSING_PORTAL_PURPOSES = ['vyjadreni', 'vytyceni'] as const;
export type NetworkCrossingPortalPurpose = (typeof NETWORK_CROSSING_PORTAL_PURPOSES)[number];

export function isNetworkCrossingPortalPurpose(
  value: string | null | undefined,
): value is NetworkCrossingPortalPurpose {
  return value === 'vyjadreni' || value === 'vytyceni';
}

/** Vytyčení sítě uses document type `network_stakeout`; everything else on this portal is a statement. */
export function networkCrossingPurposeForDocumentType(
  documentType: string | null | undefined,
): NetworkCrossingPortalPurpose {
  return (documentType ?? '').trim() === 'network_stakeout' ? 'vytyceni' : 'vyjadreni';
}

/**
 * Statement rows open `portalUrl`. Stakeout rows open `vytyceniPortalUrl` when published,
 * otherwise the same URL — callers must still record purpose `vytyceni`.
 */
export function networkCrossingPortalUrl(
  operator: NetworkCrossingOperatorPortal,
  purpose: NetworkCrossingPortalPurpose = 'vyjadreni',
): string {
  if (purpose === 'vytyceni') {
    const dedicated = (operator.vytyceniPortalUrl ?? '').trim();
    if (dedicated) return dedicated;
  }
  return operator.portalUrl;
}

/**
 * Purpose of an inbound operator mail. Null when the text does not say, or says both
 * (leave for classification — do not auto-approve either obligation).
 */
export function inferNetworkCrossingPortalPurpose(
  text: string | null | undefined,
): NetworkCrossingPortalPurpose | null {
  const folded = foldNetworkCrossingName(text);
  if (!folded) return null;
  const stakeout = folded.includes('vytyceni');
  const statement =
    folded.includes('vyjadreni') ||
    folded.includes('existenci siti') ||
    folded.includes('sdeleni o existenci') ||
    folded.includes('poloze site');
  if (stakeout === statement) return null;
  return stakeout ? 'vytyceni' : 'vyjadreni';
}

export function extractPortalRequestIds(
  text: string,
  operator?: NetworkCrossingOperatorPortal | null,
): string[] {
  const hay = String(text ?? '');
  if (!hay.trim()) return [];
  const patterns = operator?.requestIdPatterns?.length
    ? operator.requestIdPatterns
    : [
        'cislo\\s+zadosti[:\\s]+([A-Z0-9\\-/]+)',
        'číslo\\s+žádosti[:\\s]+([A-Z0-9\\-/]+)',
      ];
  const found = new Set<string>();
  for (const pattern of patterns) {
    try {
      const re = new RegExp(pattern, 'gi');
      let match: RegExpExecArray | null;
      while ((match = re.exec(hay)) != null) {
        const id = (match[1] ?? '').trim();
        if (id.length >= 4) found.add(id);
      }
    } catch {
      // ignore invalid pattern
    }
  }
  return [...found];
}

/** Env: zapne portál u známých provozovatelů z katalogu (neznámé subjekty = e-mail/datovka). */
export const AUTHORITY_NETWORK_CROSSING_PORTAL_ENABLED_ENV =
  'AUTHORITY_NETWORK_CROSSING_PORTAL_ENABLED';

export function isNetworkCrossingPortalEnabled(
  env: NodeJS.ProcessEnv | Record<string, string | undefined> = process.env,
): boolean {
  // Default ON — katalogoví provozovatelé jdou přes portál; obec / neznámý subjekt = e-mail/datovka.
  const raw = String(env[AUTHORITY_NETWORK_CROSSING_PORTAL_ENABLED_ENV] ?? '1').trim().toLowerCase();
  return !(raw === '0' || raw === 'false' || raw === 'no' || raw === 'off');
}
